import useOutsideClick from '../../../../utils/useOutsideClick';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './MultiplySelector.module.scss';

import clx from 'classnames';
import SelectorMenu from '../SelectorMenu';
import CustomInput from '../../CustomInput';
import useWindowSize from '../../../../utils/useWindowSize';

const MultiplySelector = ({
  value,
  setValue,
  options,
  placeholder,
  title,
  withIcon = false,
  icon,
  disabled = false,
  error,
  isEmptyMessage,
  closeOnClick = false,
  readOnly = true,
  isMultiple = true,
}) => {
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isMobile } = useWindowSize();
  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (value != null && value !== '') return [value];
    return [];
  }, [value]);

  const searchString = useMemo(
    () =>
      (options || [])
        .filter((option) => selectedValues.includes(option.value))
        .map((item) => item.name)
        .join(', '),
    [options, selectedValues],
  );

  useOutsideClick(dropdownRef, () => {
    if (isDropdownOpen) setIsDropdownOpen(false);
  });

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDropdownOpen]);

  const clearData = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setValue(isMultiple ? [] : '');
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsDropdownOpen((open) => !open);
  };

  const handleMenuValue = (nextValue) => {
    if (isMultiple) {
      setValue(nextValue);
      // Multi: resta aperto di default; con closeOnClick chiude dopo la selezione.
      // defer: evita che lo stesso click “cada” sull’input sotto e riapra il menu.
      if (closeOnClick) {
        window.setTimeout(() => setIsDropdownOpen(false), 0);
      }
      return;
    }
    setValue(nextValue == null || nextValue === '' ? [] : [nextValue]);
    window.setTimeout(() => setIsDropdownOpen(false), 0);
  };

  return (
    <div
      ref={dropdownRef}
      className={clx(styles.selector_wrapper, {
        [styles['selector_wrapper_close']]: !isDropdownOpen,
        [styles.selector_wrapper_mobile]: isMobile,
        [styles.selector_wrapper_open]: isDropdownOpen,
      })}
    >
      <CustomInput
        placeholder={placeholder}
        inputValue={searchString}
        setInputValue={() => {}}
        onAction={toggleDropdown}
        title={title}
        readOnly={readOnly}
        clearData={clearData}
        withIcon={withIcon}
        icon={icon}
        disabled={disabled}
        error={error}
        height={isMobile ? '40px' : 'fit-content'}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
      />
      <div
        className={clx(styles.dropdown__menu, {
          [styles.dropdown__menu_mobile]: isMobile,
        })}
      >
        {isDropdownOpen ? (
          <SelectorMenu
            isMultiple={isMultiple}
            setIsDropdownOpen={() => setIsDropdownOpen(false)}
            options={options}
            setValue={handleMenuValue}
            value={selectedValues}
            isEmptyMessage={isEmptyMessage}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MultiplySelector;
